import React, { useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import call from "./call";
import { useDelete, usePost, usePut } from "./useCall";
import type { ManualFailure } from "./useManualFailures";
import type { Setup } from "./useSetups";

const PATH = "/projects/:projectId(uuid)/failure-groups";
const UPDATE_PATH = "/projects/:projectId(uuid)/failure-groups/:groupId(uuid)";

const failureGroupsQueryKey = (projectId: string) => ["failure-groups", projectId] as const;

export interface FailureGroup {
  id: string;
  name: string;
  items: { failureDisplayName: string }[];
}

export const useFailureDisplayNames = ({
  setups,
  manualFailures,
}: {
  setups: Setup[];
  manualFailures: ManualFailure[];
}) =>
  useMemo(() => {
    const names = new Set<string>();
    for (const setup of setups) {
      for (const failure of setup["failures"]) {
        const displayName = failure.displayName ?? failure.name;
        if (displayName) names.add(displayName);
      }
    }
    for (const manualFailure of manualFailures) {
      if (manualFailure.name) names.add(manualFailure.name);
    }
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [setups, manualFailures]);

export const useFailureGroups = (projectId: string, options?: { enabled?: boolean }) =>
  useQuery({
    queryKey: failureGroupsQueryKey(projectId),
    queryFn: () =>
      call("GET", PATH, {
        routeParams: { projectId },
      }).then((response: { body: FailureGroup[] }) => response.body),
    enabled: options?.enabled,
  }).data ?? [];

export const useFailureGroupDisplayNameMap = (
  projectId: string,
  options?: { enabled?: boolean },
) => {
  const failureGroups = useFailureGroups(projectId, options);

  return React.useMemo(() => {
    const mapping: Record<string, string> = {};
    for (const group of failureGroups) {
      for (const item of group.items) {
        mapping[item.failureDisplayName] = group.name;
      }
    }
    return {
      groupByFailureDisplayName: mapping,
      hasFailureGroups: Object.keys(mapping).length > 0,
    };
  }, [failureGroups]);
};

export const useFailureGroupsMutations = (projectId: string) => {
  const createFailureGroup = usePost(PATH);
  const updateFailureGroup = usePut(UPDATE_PATH);
  const deleteFailureGroup = useDelete(UPDATE_PATH);
  const queryClient = useQueryClient();

  const revalidateFailureGroups = React.useCallback(
    () =>
      queryClient.invalidateQueries({
        queryKey: failureGroupsQueryKey(projectId),
      }),
    [projectId, queryClient],
  );

  return {
    createFailureGroup,
    updateFailureGroup,
    deleteFailureGroup,
    revalidateFailureGroups,
  };
};
